import React, {
  ChangeEvent,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Autocomplete as MuiAutocomplete,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Switch,
  TextField as MuiTextField,
  RadioGroup,
  Radio,
  IconButton,
  Popper,
} from '@mui/material';
import styles from './TextField.module.scss';
import cx from 'classnames';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import moment from 'moment';
import Alert from '../Alert/Alert';
import useDelayedSearch, {
  UseDelayedSearch,
} from '../../hooks/useDelayedSearch/useDelayedSearch';
import { Asset } from '../../domain/Asset';
import Button from '../Button/Button';
import { InputProps as StandardInputProps } from '@mui/material/Input/Input';
import Loader from '../Loader/Loader';
import { translate } from '../../utility/messageTranslator/translate';
import { useIntl } from 'react-intl';
import { Editor } from '@tinymce/tinymce-react';
import FileInput from '../FileInput/FileInput';
import { HttpError } from '../../config/Axios/axios-instance';
import * as assetService from '../../store/asset/service';
import { enGB, lt } from 'date-fns/esm/locale';
import { StoreState } from '../../config/StoreProvider/StoreProvider';
import { connect } from 'react-redux';
import { Locale } from '../../domain/Translation';
import { SignUpSectionType } from '../../pages/Public/SignUpPage/SignUpPage';
import { TypeAnimation } from 'react-type-animation';
import ProfileImageInput from '../ProfileImageInput/ProfileImageInput';
import { ReactSVG } from 'react-svg';
import calendarIcon from '../../assets/icons/calendar.svg';
import { Controlled as CodeMirror } from 'react-codemirror2';
import 'codemirror/lib/codemirror.css';
import 'codemirror/addon/display/placeholder';
import _ from 'lodash';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { FormInput } from '../../hooks/useForm/useForm';
import infoIcon from '../../assets/icons/info-circle3.svg';
import { Popover } from '../Popover/Popover';

export type AutocompleteOptionProps = {
  label: string;
  value: string;
  customLabelNameInOptionList?: string;
};

type RadioInput = {
  value?: number | string;
  label?: string;
};

export type RadioProps = {
  value: string;
  radioInputs: RadioInput[];
};

export type TextFieldProps = {
  selectedLocale: Locale;
  onChange?: (event: ChangeEvent<any>) => void;
  onFileChange?: (event: ChangeEvent<any>) => void;
  onCheckboxChange?: (name: string, value: string) => void;
  onInputValueChange?: (name: string, value: string) => void;
  onTimeChange?: (name: string, value: string) => void;
  onBlur?: (event: ChangeEvent<any>, freeSolo?: boolean) => void;
  onInputBlur?: (name: string) => void;
  onImageDelete?: (name: string, value: string | File) => void;
  onSetValidationErrors?: (error: HttpError) => void;
  value?: string | string[] | File | File[] | any[] | null;
  label?: string | ReactNode;
  errors?: Array<string>;
  dynamicInputErrors?: Record<string, any>[];
  name: string;
  type?: string;
  placeholder?: string;
  inputProps?: Partial<StandardInputProps>;
  className?: string;
  options?: Array<{
    value: string;
    label: string;
    image?: ReactNode;
    customLabelNameInOptionList?: string;
    optionLabelClassName?: string;
  }>;
  labelPlacement?: 'end' | 'start' | 'top' | 'bottom';
  variant?: 'filled' | 'outlined' | 'standard';
  isOutlined?: boolean;
  disabled?: boolean;
  helperText?: string;
  clearable?: boolean;
  multiple?: boolean;
  disableCloseOnSelect?: boolean;
  disableClearable?: boolean;
  onSelectChange?: (
    value: string | string[],
    name: string,
    freeSolo?: boolean,
  ) => void;
  isLoading?: boolean;
  onSearch?: (searchValue: string) => void;
  moreLabel?: string;
  asset?: Asset | null;
  creatable?: boolean;
  onCreate?: () => void;
  autoFocus?: boolean;
  isSmall?: boolean;
  maxFileCount?: number;
  onFileDropped?: (name: string, files: File[]) => void;
  freeSolo?: boolean;
  limit?: number;
  onShowMoreClick?: () => void;
  inputLabelProps?: {
    shrink?: boolean;
    sx?: {
      marginTop?: string;
    };
  };
  richTextProps?: {
    toolbar?: string;
    menubar?: boolean;
    height?: number;
    paste_data_images?: boolean;
  };
  maxLimit?: number;
  forgotPassword?: {
    label: string;
    section: SignUpSectionType;
  };
  codeProps?: {
    highlightedLines: number[];
  };
  onChangeSection?: (state: SignUpSectionType) => void;
  radioProps?: RadioProps;
  onRadioChange?: (name: string, value: string) => void;
  hasTypeAnimation?: boolean;
  isAssessmentContainerVisible?: boolean;
  isDateAutoCompleteDisabled?: boolean;
  minimalistic?: boolean;
  required?: boolean;
  endContainer?: ReactNode;
  startContainer?: ReactNode;
  popperClassName?: string;
  noLabels?: boolean;
  fileNameAsLabel?: boolean;
  isExcelLogo?: boolean;
  checkboxContainer?: ReactNode;
  useOnChangeInAutocompleteTextField?: boolean;
  onFocus?: () => void;
  disableSelectTyping?: boolean;
  acceptedFileTypes?: string[];
  fileInfo?: string;
  showCheckboxError?: boolean;
  showHelperElement?: boolean;
  helperElement?: ReactNode;
  dynamicConfig?: FormInput[];
  onDynamicChange?: (
    value: Record<string, any>[],
    dynamicConfig: FormInput[],
    name: string,
  ) => void;
  addButtonLabel?: string;
  addButtonInfo?: string;
  removeButtonLabel?: string;
  unchangeableRowCount?: number;
  maxFileSize?: number;
};

const TextField = ({
  selectedLocale,
  onChange,
  onBlur,
  value,
  label,
  errors,
  name,
  type,
  placeholder,
  inputProps,
  className,
  onFileChange,
  onCheckboxChange,
  options,
  labelPlacement,
  onTimeChange,
  disabled,
  helperText,
  clearable,
  multiple,
  disableCloseOnSelect,
  disableClearable,
  onSelectChange,
  isLoading = false,
  onSearch,
  moreLabel,
  asset,
  creatable,
  onCreate,
  autoFocus,
  variant = 'outlined',
  isSmall,
  onInputBlur,
  onInputValueChange,
  maxFileCount,
  onImageDelete,
  onSetValidationErrors,
  onFileDropped,
  freeSolo,
  limit,
  onShowMoreClick,
  richTextProps,
  inputLabelProps,
  maxLimit,
  forgotPassword,
  onChangeSection,
  radioProps,
  onRadioChange,
  hasTypeAnimation,
  isAssessmentContainerVisible,
  isDateAutoCompleteDisabled,
  minimalistic,
  required,
  endContainer,
  startContainer,
  popperClassName,
  noLabels,
  fileNameAsLabel,
  isExcelLogo,
  checkboxContainer,
  useOnChangeInAutocompleteTextField,
  onFocus,
  disableSelectTyping,
  acceptedFileTypes,
  fileInfo,
  showCheckboxError,
  showHelperElement,
  helperElement,
  codeProps,
  dynamicConfig,
  dynamicInputErrors,
  onDynamicChange,
  addButtonLabel,
  addButtonInfo,
  removeButtonLabel,
  unchangeableRowCount = 0,
  maxFileSize,
}: TextFieldProps) => {
  const [autocompleteInput, setAutocompleteInput] = useState<
    string | undefined
  >(undefined);

  const [isDateOpenPickerOpen, setDateOpenPickerOpen] =
    useState<boolean>(false);

  const [highlightedLines, setHighlightedLines] = useState<number[]>([]);

  const intl = useIntl();

  const showErrors = useMemo(() => variant !== 'filled', [variant]);

  const adapterLocale = useMemo(() => {
    if (selectedLocale === 'lt') {
      return lt;
    }

    return enGB;
  }, [selectedLocale]);

  const { setSearchValue }: UseDelayedSearch = useDelayedSearch(
    !!onSearch ? onSearch : () => {},
  );

  useEffect(() => {
    if (type === 'autocomplete') {
      // @ts-ignore next-line
      setSearchValue(autocompleteInput);
    }
  }, [autocompleteInput]);

  useEffect(() => {
    if (type === 'richtext') {
      document.addEventListener('focusin', handleRichTextBoxFocusIn);
    }
  }, []);

  useEffect(() => {
    return () =>
      document.removeEventListener('focusin', handleRichTextBoxFocusIn);
  }, []);

  const handleRichTextBoxFocusIn = (event: FocusEvent) => {
    if (
      event.target &&
      // @ts-ignore
      event.target.closest(
        '.tox-tinymce-aux, .moxman-window, .tam-assetmanager-root',
      ) !== null
    ) {
      event.stopImmediatePropagation();
    }
  };

  if (type === 'autocomplete') {
    const getSelectionValue = () => {
      if (Array.isArray(value)) {
        return options?.filter((option) =>
          value.toString().includes(option.value),
        );
      }

      if (multiple) {
        const values = value?.toString().split(',');
        return options?.filter(
          (option) =>
            !!values?.find(
              (singleValue) =>
                singleValue?.toString() === option.value?.toString(),
            ),
        );
      }

      return options?.find(
        (option) => option.value?.toString() === value?.toString(),
      );
    };

    const getOptions = () => {
      if (!options) {
        return [];
      }

      if (moreLabel) {
        return [...options, { label: moreLabel, value: '' }];
      }

      return options;
    };

    const renderTags = (value: any[], getTagProps: Function) => {
      if (!limit) {
        return;
      }

      const numTags = value.length;

      return (
        <>
          {value.slice(0, limit).map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={index}
              label={option.label}
            />
          ))}

          {numTags > limit && (
            <Chip
              className={styles.showMoreChip}
              label={translate(intl, 'TEXT_FIELD.SHOW_MORE').replace(
                ':count',
                (numTags - limit).toString(),
              )}
              onClick={onShowMoreClick}
            />
          )}
        </>
      );
    };

    const getOptionDisabled = () => {
      const value = getSelectionValue();

      return Array.isArray(value) && value.length === maxLimit;
    };

    const getFreeSoloValue = () => {
      if (!maxLimit) {
        return freeSolo;
      }

      const value = getSelectionValue();

      if (!Array.isArray(value)) {
        return;
      }

      return !(value.length === maxLimit);
    };

    const getTextFieldStartAdorment = () => {
      if (!startContainer && !inputProps?.startAdornment) {
        return;
      }

      return { startAdornment: startContainer || inputProps?.startAdornment };
    };

    return (
      <MuiAutocomplete
        onKeyDown={(e) => {
          if (disableSelectTyping) {
            if (
              e.key === 'Backspace' ||
              e.key === 'Delete' ||
              e.key === 'ArrowLeft'
            ) {
              e.stopPropagation();
            }

            e.preventDefault();
          }
        }}
        id={name}
        freeSolo={getFreeSoloValue()}
        className={styles.autocomplete}
        multiple={multiple}
        options={getOptions()}
        getOptionLabel={(option: any) => option.label ?? autocompleteInput}
        value={getSelectionValue() ?? null}
        disableCloseOnSelect={disableCloseOnSelect}
        disabled={disabled}
        disableClearable={disableClearable}
        getOptionDisabled={maxLimit ? getOptionDisabled : undefined}
        inputValue={multiple ? autocompleteInput : undefined}
        renderTags={limit ? renderTags : undefined}
        autoHighlight={true}
        renderOption={(props, option, { index }) =>
          option.image ? (
            <li
              key={`render-image-option-${index}`}
              className={styles.optionsWithImage}
              {...props}
            >
              {option.image}
              <p
                className={cx(styles.optionLabel, option?.optionLabelClassName)}
              >
                {option.label}
              </p>
            </li>
          ) : (
            <li {...props} key={`render-option-${index}`}>
              {option?.customLabelNameInOptionList ?? option.label}
            </li>
          )
        }
        PopperComponent={(props) => (
          <Popper {...props} className={popperClassName} />
        )}
        onChange={(_: any, values: any) => {
          setAutocompleteInput('');
          if (Array.isArray(values)) {
            return onSelectChange?.(
              (values as AutocompleteOptionProps[] | string[]).map((value) => {
                if (typeof value === 'string') {
                  return value;
                }

                return value.value;
              }),
              name,
              freeSolo,
            );
          }

          if (typeof values === 'object') {
            return onSelectChange?.(values?.value || '', name, freeSolo);
          }

          onSelectChange?.(values?.valueOf() || '', name, freeSolo);
        }}
        onBlur={(e) => {
          onBlur?.(e, freeSolo);

          if (freeSolo) {
            setAutocompleteInput('');
          }
        }}
        loading={isLoading}
        renderInput={(params: any) => (
          <MuiTextField
            {...params}
            label={label}
            placeholder={placeholder}
            helperText={
              showErrors && errors && errors.length > 0 ? errors[0] : ''
            }
            disableCloseOnSelect
            error={errors && errors.length > 0}
            name={name}
            className={cx(styles.textField, styles.textField, className)}
            variant={variant as any}
            size={isSmall ? 'small' : 'medium'}
            onFocus={onFocus}
            onChange={(event) => {
              setAutocompleteInput(event.target.value);
              useOnChangeInAutocompleteTextField && onChange?.(event);
            }}
            InputLabelProps={inputLabelProps}
            InputProps={{
              ...params.InputProps,
              disableUnderline: variant === 'filled' && !errors?.length,
              endAdornment: (
                <React.Fragment>
                  {isLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {creatable && (
                    <Button buttonVariant="text" onClick={() => onCreate?.()}>
                      {translate(intl, 'INPUTS.CREATE')}
                    </Button>
                  )}
                  {endContainer && endContainer}
                </React.Fragment>
              ),
              ...getTextFieldStartAdorment(),
            }}
          />
        )}
      />
    );
  }

  if (type === 'file') {
    return (
      <FileInput
        value={value as File | File[] | string | string[] | undefined}
        name={name}
        helperText={helperText}
        label={label}
        onFileChange={onFileChange}
        maxFileCount={maxFileCount}
        multiple={multiple}
        errors={errors}
        asset={asset}
        onImageDelete={onImageDelete}
        onSetValidationErrors={onSetValidationErrors}
        onFileDropped={onFileDropped}
        minimalistic={minimalistic}
        noLabels={noLabels}
        fileNameAsLabel={fileNameAsLabel}
        isExcelLogo={isExcelLogo}
        acceptedFileTypes={acceptedFileTypes}
        fileInfo={fileInfo}
        maxFileSize={maxFileSize}
        className={className}
      />
    );
  }

  if (type === 'profileImage') {
    return (
      <ProfileImageInput
        value={value as File}
        name={name}
        onFileChange={onFileChange}
        errors={errors}
        disabled={disabled}
      />
    );
  }

  if (type === 'currency') {
    return (
      <>
        {helperText && <Alert variant="warning">{helperText}</Alert>}
        <MuiTextField
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          label={label}
          error={errors && errors.length > 0}
          variant={variant as any}
          name={name}
          helperText={
            showErrors && errors && errors.length > 0 ? errors[0] : ''
          }
          className={cx(styles.textField, styles.currencyInput, className)}
          type="number"
          disabled={disabled}
          placeholder={placeholder}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          InputProps={{
            disableUnderline: variant === 'filled' && !errors?.length,
            startAdornment: <InputAdornment position="start">€</InputAdornment>,
          }}
          size={isSmall ? 'small' : 'medium'}
        />
      </>
    );
  }

  if (type === 'time') {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <TimePicker
          ampm={false}
          value={
            typeof value === 'string' ? moment(value, 'HH:mm').toDate() : ''
          }
          onChange={(newValue: any) => {
            onTimeChange &&
              onTimeChange(name, moment(newValue?.toString()).format('HH:mm'));
          }}
          label={label}
          disabled={disabled}
          InputProps={{
            disableUnderline: variant === 'filled' && !errors?.length,
          }}
          renderInput={(params: any) => (
            <MuiTextField
              {...params}
              className={cx(styles.textField, styles.textField, className)}
              onBlur={onBlur}
              helperText={
                showErrors && errors && errors.length > 0 ? errors[0] : ''
              }
              error={errors && errors.length > 0}
              variant={variant as any}
            />
          )}
        />
      </LocalizationProvider>
    );
  }

  if (type === 'staticdate') {
    return (
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={adapterLocale}
      >
        <StaticDatePicker
          displayStaticWrapperAs="desktop"
          openTo="day"
          minDate={moment().startOf('year').format('YYYY-MM-DD')}
          maxDate={moment().add(1, 'month').format('YYYY-MM-DD')}
          label={label}
          value={value}
          onChange={(newValue: any) => {
            onTimeChange &&
              onTimeChange(
                name,
                newValue
                  ? moment(newValue.toString()).format('YYYY-MM-DD')
                  : '',
              );
          }}
          InputProps={{
            disableUnderline: variant === 'filled' && !errors?.length,
          }}
          className={styles.staticDateContainer}
          renderInput={(params: any) => (
            <MuiTextField
              {...params}
              className={cx(styles.textField, className)}
              onBlur={onBlur}
              name={name}
              helperText={
                showErrors && errors && errors.length > 0 ? errors[0] : ''
              }
              error={errors && errors.length > 0}
              variant={variant as any}
              size={isSmall ? 'small' : 'medium'}
            />
          )}
        />
      </LocalizationProvider>
    );
  }

  if (type === 'datetime') {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DateTimePicker
          views={['year', 'month', 'day', 'hours', 'minutes']}
          label={label}
          value={
            value
              ? moment
                  .utc(value as string)
                  .local()
                  .toDate()
              : null
          }
          onChange={(newValue: any) => {
            onTimeChange &&
              onTimeChange(
                name,
                newValue
                  ? moment(newValue).utc().format('YYYY-MM-DD HH:mm')
                  : '',
              );
          }}
          mask="____-__-__ __:__"
          disabled={disabled}
          inputFormat="yyyy-MM-dd HH:mm"
          InputProps={{
            disableUnderline: variant === 'filled' && !errors?.length,
          }}
          renderInput={(params: any) => (
            <MuiTextField
              {...params}
              className={cx(styles.textField, styles.textField, className)}
              onBlur={onBlur}
              name={name}
              helperText={
                showErrors && errors && errors.length > 0 ? errors[0] : ''
              }
              error={errors && errors.length > 0}
              variant={variant as any}
              size={isSmall ? 'small' : 'medium'}
              InputLabelProps={inputLabelProps}
            />
          )}
        />
      </LocalizationProvider>
    );
  }

  if (type === 'date') {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DateTimePicker
          views={['year', 'month', 'day']}
          label={label}
          value={value}
          onChange={(newValue: any) => {
            onTimeChange &&
              onTimeChange(
                name,
                newValue
                  ? moment(newValue.toString()).format('YYYY-MM-DD')
                  : '',
              );
          }}
          mask="____-__-__"
          disabled={disabled}
          inputFormat="yyyy-MM-dd"
          InputProps={{
            disableUnderline: variant === 'filled' && !errors?.length,
            startAdornment: (
              <InputAdornment position="start">
                <IconButton onClick={() => setDateOpenPickerOpen(true)}>
                  <ReactSVG src={calendarIcon} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          open={isDateOpenPickerOpen}
          onOpen={() => setDateOpenPickerOpen(true)}
          onClose={() => setDateOpenPickerOpen(false)}
          PaperProps={{
            className: styles.dateContainer,
          }}
          DialogProps={{
            className: styles.dateContainer,
          }}
          disableOpenPicker
          renderInput={(params: any) => (
            <MuiTextField
              {...params}
              className={cx(styles.textField, styles.dateInput, className)}
              onBlur={onBlur}
              name={name}
              helperText={
                showErrors && errors && errors.length > 0 ? errors[0] : ''
              }
              error={errors && errors.length > 0}
              variant={variant as any}
              size={isSmall ? 'small' : 'medium'}
              autoComplete={isDateAutoCompleteDisabled ? 'off' : 'on'}
              inputProps={{
                ...params.inputProps,
                ...(placeholder && { placeholder }),
              }}
            />
          )}
        />
      </LocalizationProvider>
    );
  }

  if (type === 'switcher') {
    return (
      <FormControlLabel
        className={cx(styles.switcherContainer, className)}
        labelPlacement={labelPlacement}
        control={
          <Switch
            name={name}
            checked={value === '1'}
            onChange={() =>
              onCheckboxChange &&
              onCheckboxChange(name, value === '0' ? '1' : '0')
            }
          />
        }
        disabled={disabled}
        label={label}
      />
    );
  }

  if (type === 'checkbox') {
    return (
      <>
        <FormControlLabel
          className={cx(styles.checkboxContainer, className)}
          labelPlacement={labelPlacement}
          control={
            <Checkbox
              checked={value === '1'}
              onChange={() =>
                onCheckboxChange &&
                onCheckboxChange(name, value === '0' ? '1' : '0')
              }
            />
          }
          disabled={disabled}
          label={checkboxContainer ?? label}
        />
        {showCheckboxError && errors && errors.length > 0 && (
          <div className={styles.checkboxError}>{errors[0]}</div>
        )}
      </>
    );
  }

  if (type === 'select') {
    return (
      <MuiTextField
        value={value}
        select
        onChange={onChange}
        onBlur={onBlur}
        label={label}
        error={errors && errors.length > 0}
        variant={variant as any}
        name={name}
        helperText={showErrors && errors && errors.length > 0 ? errors[0] : ''}
        className={cx(styles.textField, className)}
        type="number"
        placeholder={placeholder}
        size={isSmall ? 'small' : 'medium'}
        InputLabelProps={inputLabelProps}
      >
        {options?.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        )) ?? []}
      </MuiTextField>
    );
  }

  if (type === 'richtext') {
    return (
      <div
        className={cx(styles.editorContainer, {
          [styles.error]: errors && errors.length,
        })}
      >
        <Editor
          apiKey={process.env.REACT_APP_TINYMCE_KEY}
          disabled={disabled}
          value={value?.toString()}
          onEditorChange={(value) =>
            onInputValueChange && onInputValueChange(name, value)
          }
          onBlur={(event) => {
            event.preventDefault();

            onInputBlur && onInputBlur(name);
          }}
          init={{
            automatic_uploads: true,
            file_picker_types: 'image',
            plugins: 'image | autoresize',
            toolbar:
              'undo redo | formatselect | ' +
              'bold italic backcolor image | alignleft aligncenter ' +
              'alignright alignjustify | bullist numlist outdent indent | ' +
              'removeformat | help',
            images_upload_handler: async (blobInfo) => {
              return new Promise(async (resolve, reject) => {
                await assetService.createAsset(
                  blobInfo.blob(),
                  resolve,
                  reject,
                );
              });
            },
            ...richTextProps,
          }}
        />
        {errors &&
          errors.map((error, index) => (
            <div className={styles.editorError} key={`editor-error-${index}`}>
              {error}
            </div>
          ))}
      </div>
    );
  }

  if (type === 'radio' && radioProps && onRadioChange) {
    return (
      <RadioGroup
        value={value}
        onChange={(event) => onRadioChange(name, event.target.value)}
        name={name}
        row
      >
        {radioProps.radioInputs.map((radioInput) => (
          <FormControlLabel
            key={radioInput.value}
            value={radioInput.value}
            className={styles.radioInputContainer}
            control={<Radio />}
            label={
              hasTypeAnimation && isAssessmentContainerVisible ? (
                <TypeAnimation
                  sequence={[radioInput.label as string]}
                  speed={65}
                  repeat={1}
                  cursor={false}
                />
              ) : (
                radioInput.label
              )
            }
          />
        ))}
      </RadioGroup>
    );
  }

  if (type === 'code') {
    return (
      <div className={styles.codeInputContainer}>
        <CodeMirror
          className={styles.code}
          value={value?.toString() ?? ''}
          options={{
            lineNumbers: true,
            lineWrapping: true,
            placeholder: placeholder,
          }}
          onBeforeChange={(editor, data, val) => {
            onInputValueChange && onInputValueChange(name, val);
          }}
          onChange={(editor, data, val) => {
            onInputValueChange && onInputValueChange(name, val);

            if (
              _.isEqual(
                _.sortBy(highlightedLines),
                _.sortBy(codeProps?.highlightedLines ?? []),
              )
            ) {
              return;
            }

            highlightedLines.forEach((line) => {
              const lineHandle = editor.getLineHandle(line);
              lineHandle &&
                editor.removeLineClass(lineHandle, 'wrap', 'highlighted');
            });

            codeProps?.highlightedLines.forEach((line) => {
              const lineHandle = editor.getLineHandle(line);
              lineHandle &&
                editor.addLineClass(lineHandle, 'wrap', 'highlighted');
            });

            setHighlightedLines(codeProps?.highlightedLines ?? []);
          }}
          onBlur={(editor, e) => {
            e?.preventDefault();

            onInputBlur && onInputBlur(name);
          }}
        />
        {errors &&
          errors.map((error, index) => (
            <div className={styles.codeError} key={`code-error-${index}`}>
              {error}
            </div>
          ))}
      </div>
    );
  }

  if (type === 'dynamic') {
    const items = useMemo(
      () => (Array.isArray(value) ? (value as Record<string, any>[]) : []),
      [value],
    );

    const handleChange = (value: Record<string, any>[]) => {
      if (!dynamicConfig) {
        return;
      }

      onDynamicChange?.(value, dynamicConfig, name);
    };

    const handleAdd = () => {
      const blank = (dynamicConfig ?? []).reduce<Record<string, any>>(
        (acc, cfg) => {
          acc[cfg.name] = cfg.type === 'checkbox' ? '0' : '';
          return acc;
        },
        {},
      );

      handleChange?.([...items, blank]);
    };

    const handleRemove = (idx: number) => {
      handleChange?.(items.filter((_, i) => i !== idx));
    };

    const handleFieldChange = (rowIdx: number, field: string, value: any) => {
      const next = items.map((row, i) =>
        i === rowIdx ? { ...row, [field]: value } : row,
      );

      handleChange?.(next);
    };

    return (
      <div className={styles.dynamicContainer}>
        {items.length === 0 && (
          <div className={styles.noItemsText}>
            {translate(intl, 'TEXT_FIELD.DYNAMIC_NO_ITEMS')}
          </div>
        )}

        {items.map((row, rowIdx) => (
          <div key={rowIdx} className={styles.dynamicRow}>
            {(dynamicConfig ?? [])
              .filter((cfg) => !cfg.isHidden)
              .map((cfg) => (
                <TextField
                  key={`${rowIdx}-${cfg.name}`}
                  type={cfg.type}
                  name={cfg.name}
                  label={cfg.label}
                  placeholder={cfg.placeholder}
                  required={cfg.required}
                  options={cfg.options}
                  value={row[cfg.name]}
                  variant={variant}
                  isSmall={isSmall}
                  disabled={disabled || rowIdx < unchangeableRowCount}
                  onChange={(e) =>
                    handleFieldChange(rowIdx, cfg.name, e.target.value)
                  }
                  onCheckboxChange={(_, val) =>
                    handleFieldChange(rowIdx, cfg.name, val)
                  }
                  className={styles.dynamicField}
                  selectedLocale={selectedLocale}
                  errors={dynamicInputErrors?.[rowIdx]?.[cfg.name] || []}
                  inputLabelProps={inputLabelProps}
                />
              ))}

            {rowIdx >= unchangeableRowCount && (
              <Button
                onClick={() => handleRemove(rowIdx)}
                className={styles.removeButton}
                buttonVariant="text"
                variant="danger"
                startIcon={<RemoveIcon />}
                disableRipple
              >
                {removeButtonLabel}
              </Button>
            )}
          </div>
        ))}

        <div className={styles.addButtonContainer}>
          <Button
            buttonVariant="text"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            className={styles.addButton}
            disableRipple
          >
            {addButtonLabel}
          </Button>

          {addButtonInfo && (
            <Popover label={addButtonInfo} icon={<ReactSVG src={infoIcon} />}>
              <ReactSVG className={styles.infoIcon} src={infoIcon} />
            </Popover>
          )}
        </div>
      </div>
    );
  }

  return (
    <Loader isLoading={isLoading} size="2xs" className={styles.textFieldLoader}>
      {forgotPassword && onChangeSection && (
        <div
          className={styles.forgotPassword}
          onClick={() => onChangeSection(forgotPassword.section)}
        >
          {translate(intl, forgotPassword.label)}
        </div>
      )}
      <MuiTextField
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        label={label}
        error={errors && errors.length > 0}
        variant={variant as any}
        name={name}
        helperText={showErrors && errors && errors.length > 0 ? errors[0] : ''}
        className={cx(
          styles.textField,
          { [styles.textareaField]: type === 'textarea' },
          className,
        )}
        type={type}
        placeholder={placeholder}
        InputLabelProps={inputLabelProps}
        InputProps={{
          ...inputProps,
        }}
        multiline={type === 'textarea'}
        rows={5}
        disabled={disabled}
        autoFocus={autoFocus}
        size={isSmall ? 'small' : 'medium'}
        required={required}
      />
      {(!errors || (errors && errors.length === 0)) &&
        showHelperElement &&
        helperElement}
    </Loader>
  );
};

const mapStateToProps = (state: StoreState) => ({
  selectedLocale: state.auth.selectedLocale,
});

export default connect(mapStateToProps)(TextField);
